import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
// Redux
import { useSelector } from "react-redux";
// Constants - Functions
import { project, errorColor, bodyColor } from "../../../../config/constants";
import { isAISNProjectHospital } from "../../../../config/projectFunctions";
import { renderCivilStatusOptions, renderEducationOptions, renderWorkOptions } from "../../../../config/dropdownFunctions";
// Components
import {
  PersonalInformationDiv,
  TitleTextDiv,
  DataDiv,
  ColumnDataDiv,
  InputDataDiv,
  Label,
  Input,
  RelativeButton,
  InputStartingDateDiv,
  InputStartingDate,
  InputBirthday,
  Textarea,
  SkinColorContainer,
  HandsContainer,
  SkinColorCheckboxContainer,
  SkinColorCheckbox,
  SkinColorImg,
  SkinColorChecked,
} from "./styles";
import Select from "../../../selectInput";
// Assets
import SelectArrow from "../../../../assets/icons/ico_arrow_down.svg";
import VisiblePasswordImg from "../../../../assets/icons/btn_visible_password.svg";
import HidePasswordImg from "../../../../assets/icons/btn_hide_password.svg";
import CheckImg from "../../../../assets/images/ico_check.png";
import Hand1 from "../../../../assets/images/patients/ico_hand_skin1.png";
import Hand2 from "../../../../assets/images/patients/ico_hand_skin2.png";
import Hand3 from "../../../../assets/images/patients/ico_hand_skin3.png";
import Hand4 from "../../../../assets/images/patients/ico_hand_skin4.png";
import Hand5 from "../../../../assets/images/patients/ico_hand_skin5.png";

const PersonalInfo = ({
  hospitalClinicians,
  login,
  setLogin,
  loginError,
  setLoginError,
  password,
  setPassword,
  passwordError,
  setPasswordError,
  clinicianId,
  setClinicianId,
  aisnGroup,
  setAisnGroup,
  aisnGroupError,
  setAisnGroupError,
  startingDate,
  setStartingDate,
  startingDateError,
  setStartingDateError,
  gender,
  setGender,
  genderError,
  setGenderError,
  height,
  setHeight,
  heightError,
  setHeightError,
  dateOfBirth,
  setDateOfBirth,
  dateOfBirthError,
  setDateOfBirthError,
  civilStatus,
  setCivilStatus,
  civilStatusError,
  setCivilStatusError,
  homeSupport,
  setHomeSupport,
  homeSupportError,
  setHomeSupportError,
  education,
  setEducation,
  educationError,
  setEducationError,
  work,
  setWork,
  workError,
  setWorkError,
  gameExperience,
  setGameExperience,
  computerExperience,
  setComputerExperience,
  otherInfo,
  setOtherInfo,
  skinColor,
  setSkinColor,
}) => {
  const [visiblePassword, setVisiblePassword] = useState(false);
  const [isAISNHospital, setIsAISNHospital] = useState(false);
  const hospitalName = useSelector(({ clinician }) => clinician.data.HOSPITAL_NAME);
  const clinicianData = useSelector(({ clinician }) => clinician.data);
  const [t] = useTranslation("global");

  // Set AISN project flag
  useEffect(() => {
    const handleSetAISNProject = async () => {
      const isAISNHospital = await isAISNProjectHospital(hospitalName);
      setIsAISNHospital(isAISNHospital);
    };
    handleSetAISNProject();

    return () => {
      setIsAISNHospital(false);
    };
  }, [hospitalName]);

  const handleSetPasswordVisibility = (e) => {
    e.preventDefault();
    setVisiblePassword(!visiblePassword);
  };

  const handleSetInput = (e, setInput, setError) => {
    setInput(e.target.value);
    setError(false);
  };

  const handleSetUserInput = (e) => {
    const valueWithoutSpaces = e.target.value.replace(/\s+/g, "");
    setLogin(valueWithoutSpaces);
    setLoginError(false);
  };

  const handleSetPasswordInput = (e) => {
    const valueWithoutSpaces = e.target.value.replace(/\s+/g, "");
    setPassword(valueWithoutSpaces);
    setPasswordError(false);
  };

  const renderSkinColor = () => {
    const renderSkinColorInput = (skinColorParam, skinImg) => {
      const verifySkinColor = skinColorParam === skinColor;

      return (
        <SkinColorCheckboxContainer verifySkinColor={verifySkinColor}>
          <SkinColorCheckbox type="checkbox" value={skinColorParam} onClick={() => setSkinColor(skinColorParam)} />
          <SkinColorImg src={skinImg} alt="ico-hand" verifySkinColor={verifySkinColor} />
          {verifySkinColor && <SkinColorChecked src={CheckImg} alt="ico-check" />}
        </SkinColorCheckboxContainer>
      );
    };

    return (
      <SkinColorContainer>
        <InputDataDiv>
          <Label>{isAISNHospital ? "Avatar" : t("personalInformation_skinColorLabel")}</Label>
          <HandsContainer>
            {renderSkinColorInput("FDC3AD", Hand1)}
            {renderSkinColorInput("D09C80", Hand2)}
            {renderSkinColorInput("A37552", Hand3)}
            {renderSkinColorInput("6B4731", Hand4)}
            {renderSkinColorInput("33180F", Hand5)}
          </HandsContainer>
        </InputDataDiv>
      </SkinColorContainer>
    );
  };

  const renderSelectAISNGroup = () => {
    return (
      <InputDataDiv>
        <Label fontWeight="bold">{t("AISN Group")} *</Label>
        <Select
          value={aisnGroup}
          onChange={(e) => handleSetInput(e, setAisnGroup, setAisnGroupError)}
          padding="5px 35px 5px 10px"
          width="200px"
          arrowImg={SelectArrow}
          backgroundPosition="175px center"
          error={aisnGroupError}
          disabled={clinicianData?.AISN_GROUP === "RGS+AI"}
        >
          {clinicianData?.AISN_GROUP === "RGS+AI" ? (
            <option value={"RGS+AI"}>RGS+AI</option>
          ) : (
            <>
              <option value={""}></option>
              <option value={"RGS-AI"}>RGS-AI</option>
              <option value={"Control"}>Control</option>
            </>
          )}
        </Select>
      </InputDataDiv>
    );
  };

  const renderInputStartingDate = () => {
    const enabledDates = (date) => {
      return moment(date).isAfter(moment(), "day");
    };

    const handleSetDate = (date) => {
      setStartingDate(date);
      setStartingDateError(false);
    };

    return (
      <InputDataDiv>
        <Label htmlFor="startingDate" fontWeight="bold">
          {t(aisnGroup === "Control" ? "Starting date" : "RGS+ Starting date")} *
        </Label>
        <InputStartingDateDiv>
          <InputStartingDate
            selected={startingDate}
            onChange={(date) => handleSetDate(date)}
            filterDate={enabledDates}
            placeholderText="dd/mm/yyyy"
            dateFormat={"dd/MM/yyyy"}
            border={startingDateError ? `1px solid ${errorColor}` : `1px solid ${bodyColor}`}
          />
        </InputStartingDateDiv>
      </InputDataDiv>
    );
  };

  return (
    <PersonalInformationDiv>
      <TitleTextDiv>
        <p>{t("personalInformation_title")}</p>
      </TitleTextDiv>
      <DataDiv>
        <ColumnDataDiv width="385px">
          <InputDataDiv>
            <Label htmlFor="login" fontWeight="bold">
              {isAISNHospital ? "Patient ID" : t("username")} *
            </Label>
            <Input
              type="text"
              placeholder={t("personalInformation_userNameInput")}
              id="login"
              value={login}
              onChange={handleSetUserInput}
              border={loginError ? `1px solid ${errorColor}` : `1px solid ${bodyColor}`}
            />
          </InputDataDiv>
          <InputDataDiv>
            <Label htmlFor="password" fontWeight="bold">
              {t("password")} *
            </Label>
            <Input
              type={visiblePassword ? "text" : "password"}
              placeholder="*******"
              id="password"
              value={password}
              onChange={handleSetPasswordInput}
              border={passwordError ? `1px solid ${errorColor}` : `1px solid ${bodyColor}`}
              padding="5px 32px 5px 10px"
            />
            <RelativeButton onClick={handleSetPasswordVisibility}>
              <img src={visiblePassword ? HidePasswordImg : VisiblePasswordImg} alt="icon-password-button" />
            </RelativeButton>
          </InputDataDiv>
          <InputDataDiv>
            <Label fontWeight="bold">{t("therapist")} *</Label>
            <Select
              value={clinicianId}
              onChange={(e) => setClinicianId(e.target.value)}
              padding="5px 35px 5px 10px"
              width="200px"
              arrowImg={SelectArrow}
              backgroundPosition="175px center"
              disabled={hospitalClinicians.length === 1}
            >
              {hospitalClinicians.map((clinician) => {
                const clinicianId = clinician.CLINICIAN_ID;
                return (
                  <option key={clinicianId} value={clinicianId}>
                    {clinician.CLINICIAN_USER}
                  </option>
                );
              })}
            </Select>
          </InputDataDiv>
          {isAISNHospital && (
            <>
              {renderSelectAISNGroup()}
              {renderInputStartingDate()}
            </>
          )}
          <InputDataDiv>
            <Label fontWeight="bold">{t("patientGender_label")} *</Label>
            <Select
              value={gender}
              onChange={(e) => handleSetInput(e, setGender, setGenderError)}
              padding="5px 35px 5px 10px"
              width="200px"
              arrowImg={SelectArrow}
              backgroundPosition="175px center"
              error={genderError}
            >
              <option value=""></option>
              <option value="MALE">{t("patientGender_male")}</option>
              <option value="FEMALE">{t("patientGender_female")}</option>
            </Select>
          </InputDataDiv>
          {project === "global" && (
            <InputDataDiv>
              <Label htmlFor="height" fontWeight="bold">
                {t("personalInformation_heightLabel")} *
              </Label>
              <Input
                type="number"
                placeholder="175"
                id="height"
                value={height}
                onChange={(e) => handleSetInput(e, setHeight, setHeightError)}
                border={heightError ? `1px solid ${errorColor}` : `1px solid ${bodyColor}`}
              />
            </InputDataDiv>
          )}
          <InputDataDiv>
            <Label htmlFor="dateOfBirth" fontWeight="bold">
              {t("personalInformation_birthdayLabel")} *
            </Label>
            <InputBirthday
              format="##/##/####"
              placeholder="dd/mm/yyyy"
              mask={["d", "d", "m", "m", "y", "y", "y", "y"]}
              value={dateOfBirth}
              onChange={(e) => handleSetInput(e, setDateOfBirth, setDateOfBirthError)}
              border={dateOfBirthError ? `1px solid ${errorColor}` : `1px solid ${bodyColor}`}
            />
          </InputDataDiv>

          {isAISNHospital && (
            <InputDataDiv>
              <Label fontWeight="bold">{t("Civil status")} *</Label>
              <Select
                value={civilStatus}
                onChange={(e) => handleSetInput(e, setCivilStatus, setCivilStatusError)}
                padding="5px 35px 5px 10px"
                width="200px"
                arrowImg={SelectArrow}
                backgroundPosition="175px center"
                error={civilStatusError}
              >
                {renderCivilStatusOptions(t)}
              </Select>
            </InputDataDiv>
          )}

          {project === "strack" && renderSkinColor()}
        </ColumnDataDiv>
        <ColumnDataDiv width="385px">
          {isAISNHospital && (
            <>
              <InputDataDiv>
                <Label fontWeight="bold">{t("Home support")} *</Label>
                <Select
                  value={homeSupport}
                  onChange={(e) => handleSetInput(e, setHomeSupport, setHomeSupportError)}
                  padding="5px 35px 5px 10px"
                  width="200px"
                  arrowImg={SelectArrow}
                  backgroundPosition="175px center"
                  error={homeSupportError}
                >
                  <option value=""></option>
                  <option value={0}>{t("no")}</option>
                  <option value={1}>{t("yes")}</option>
                </Select>
              </InputDataDiv>
              <InputDataDiv>
                <Label fontWeight="bold">{t("Education")} *</Label>
                <Select
                  value={education}
                  onChange={(e) => handleSetInput(e, setEducation, setEducationError)}
                  padding="5px 35px 5px 10px"
                  width="200px"
                  arrowImg={SelectArrow}
                  backgroundPosition="175px center"
                  error={educationError}
                >
                  {renderEducationOptions(t)}
                </Select>
              </InputDataDiv>
              <InputDataDiv>
                <Label fontWeight="bold">{t("Work")} *</Label>
                <Select
                  value={work}
                  onChange={(e) => handleSetInput(e, setWork, setWorkError)}
                  padding="5px 35px 5px 10px"
                  width="200px"
                  arrowImg={SelectArrow}
                  backgroundPosition="175px center"
                  error={workError}
                >
                  {renderWorkOptions(t)}
                </Select>
              </InputDataDiv>
            </>
          )}
          <InputDataDiv>
            <Label>{t("personalInformation_videoGameExperienceLabel")}</Label>
            <Select
              value={gameExperience}
              onChange={(e) => setGameExperience(e.target.value)}
              padding="5px 35px 5px 10px"
              width="200px"
              arrowImg={SelectArrow}
              backgroundPosition="175px center"
            >
              <option value={null}>{t("no")}</option>
              <option value={1}>{t("yes")}</option>
            </Select>
          </InputDataDiv>
          <InputDataDiv>
            <Label>{t("personalInformation_computerExperienceLabel")}</Label>
            <Select
              value={computerExperience}
              onChange={(e) => setComputerExperience(e.target.value)}
              padding="5px 35px 5px 10px"
              width="200px"
              arrowImg={SelectArrow}
              backgroundPosition="175px center"
            >
              <option value={null}>{t("no")}</option>
              <option value={1}>{t("yes")}</option>
            </Select>
          </InputDataDiv>
          <InputDataDiv alignItems="flex-start">
            <Label htmlFor="comment">{t("personalInformation_otherInformationLabel")}</Label>
            <Textarea id="comment" value={otherInfo} onChange={(e) => setOtherInfo(e.target.value)} />
          </InputDataDiv>
          {project === "global" && renderSkinColor()}
        </ColumnDataDiv>
      </DataDiv>
    </PersonalInformationDiv>
  );
};

export default PersonalInfo;
